/* The skills hub: the catalog, one skill, one publisher's repo, and the workspace's own
   skills coming in — an upload, the editor, a GitHub link, or a word to Definable
   (tasks/custom-skills/01-design-mock.md). Putting a skill on an agent is the agent's
   Settings. */
import React, { useState } from "react";

import AskDefinableDialog from "./components/AskDefinableDialog";
import GithubDialog from "./components/GithubDialog";
import PublisherPage from "./components/PublisherPage";
import SkillDetail from "./components/SkillDetail";
import SkillEditor from "./components/SkillEditor";
import SkillsCatalog from "./components/SkillsCatalog";
import UploadSkill from "./components/UploadSkill";
import { keptDraft, useAccount } from "./keptDraft";

import type { NewSkillWay } from "./components/NewSkillMenu";

type Place =
  | { view: "catalog" }
  | { view: "skill"; id: string }
  | { view: "repo"; repo: string }
  | { view: "upload" }
  | { view: "write" }
  | { view: "edit"; id: string };

export default function SkillsPage({ onAskDefinable, onOpenAgent }: {
  /** Opens a Definable chat with this as its first message; absent, there is no Definable to ask. */
  onAskDefinable?: (prompt: string) => void;
  /** Opens an agent's Settings, where its skills are. */
  onOpenAgent?: (agentId: string) => void;
}) {
  // unsaved editor work from an earlier visit opens again, where it was left (the app
  // mounts this page once settings are read, so the account is known here)
  const account = useAccount();
  const [place, setPlace] = useState<Place>(() => {
    const kept = keptDraft(account);
    return !kept ? { view: "catalog" } : kept.skillId ? { view: "edit", id: kept.skillId } : { view: "write" };
  });
  const [back, setBack] = useState<Place>({ view: "catalog" });
  const [dialog, setDialog] = useState<"ask" | "github" | null>(null);

  const openSkill = (id: string) => {
    setBack(place.view === "repo" ? place : { view: "catalog" });
    setPlace({ view: "skill", id });
  };
  const openRepo = (repo: string) => setPlace({ view: "repo", repo });
  const toCatalog = () => setPlace({ view: "catalog" });
  const startNew = (way: NewSkillWay) => {
    if (way === "upload" || way === "write") setPlace({ view: way });
    else setDialog(way);
  };

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)]">
      {place.view === "skill" && (
        <SkillDetail
          key={place.id}
          id={place.id}
          onBack={() => setPlace(back)}
          onOpenRepo={openRepo}
          onEdit={() => setPlace({ view: "edit", id: place.id })}
          onDeleted={toCatalog}
          onOpenAgent={onOpenAgent}
        />
      )}
      {place.view === "repo" && (
        <PublisherPage key={place.repo} repo={place.repo} onBack={toCatalog} onOpen={openSkill} onOpenRepo={openRepo} />
      )}
      {place.view === "upload" && <UploadSkill onBack={toCatalog} onSaved={openSkill} />}
      {place.view === "write" && <SkillEditor onBack={toCatalog} onSaved={openSkill} />}
      {place.view === "edit" && (
        <SkillEditor key={place.id} editId={place.id} onBack={() => setPlace({ view: "skill", id: place.id })} onSaved={openSkill} />
      )}
      {/* Kept mounted behind a skill, so going back keeps the search, filters and pages */}
      <div className={place.view === "catalog" ? "flex-1 flex flex-col min-w-0 min-h-0" : "hidden"}>
        <SkillsCatalog onOpen={openSkill} onOpenRepo={openRepo} onNew={startNew} canAsk={!!onAskDefinable} />
      </div>

      {dialog === "github" && <GithubDialog onClose={() => setDialog(null)} onSaved={(id) => { setDialog(null); openSkill(id); }} />}
      {dialog === "ask" && onAskDefinable && (
        <AskDefinableDialog onClose={() => setDialog(null)} onAsk={(prompt) => { setDialog(null); onAskDefinable(prompt); }} />
      )}
    </main>
  );
}
